// src/pages/LabReferralbroker.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const LabReferral = () => {
  const [activeSubTab, setActiveSubTab] = useState("all");

  const getCurrentDate = () => new Date().toISOString().split("T")[0];

  const [brokerOptions, setBrokerOptions] = useState([]);
  const [brokerId, setBrokerId] = useState("");
  const [brokerLabel, setBrokerLabel] = useState("");
  const [loadingBrokers, setLoadingBrokers] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ Hold the full broker list so we can look up mobile & address by broker_id
  const [brokerMasterList, setBrokerMasterList] = useState([]);

  const [formData, setFormData] = useState({
    search_key: "",
    from: getCurrentDate(),
    to: getCurrentDate(),
    lab_reg_referral_id: "",
    lab_reg_referral: "",
    lab_reg_broker_id: "",
    lab_reg_broker_name: "",
    department_filter: "all",
    records: [],
  });

  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payProcessing, setPayProcessing] = useState(false);
  const [payError, setPayError] = useState("");

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "";
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const getBrokerNumbers = (row) => {
    if (!row) return { commission: 0, paid: 0, due: 0 };
    const num = (v) => {
      const n = parseFloat(v ?? 0);
      return Number.isNaN(n) ? 0 : n;
    };
    const commission = num(row.lab_reg_broker_commission ?? row.broker_commission);
    let paid = num(row.lab_reg_broker_commission_paid ?? row.broker_commission_paid);
    if (row.broker_payments_sum != null) paid = Math.max(paid, num(row.broker_payments_sum));
    const due = Math.max(commission - paid, 0);
    return { commission, paid, due };
  };

  const getBrokerStatusInfo = (row) => {
    const { commission, paid, due } = getBrokerNumbers(row);
    if (commission <= 0) return { status: "N/A", className: "bg-secondary" };
    if (paid <= 0) return { status: "Not Paid", className: "bg-danger" };
    if (due <= 0.01) return { status: "Paid", className: "bg-success" };
    return { status: "Partial", className: "bg-warning text-dark" };
  };

  // ✅ Build a lookup map: broker_id -> { mobile, address }
  const brokerInfoMap = useMemo(() => {
    const map = new Map();
    (brokerMasterList || []).forEach((b) => {
      const id = String(b.broker_id ?? b.id ?? b.user_id ?? "");
      if (!id) return;
      map.set(id, {
        mobile: b.mobile || "",
        address: b.address || "",
      });
    });
    return map;
  }, [brokerMasterList]);

  // ✅ Helper to pull mobile from row OR fallback to master list
  const getBrokerMobile = (row) => {
    if (!row) return "";
    if (row.broker_mobile) return row.broker_mobile;
    if (row.lab_reg_broker_mobile) return row.lab_reg_broker_mobile;
    const info = brokerInfoMap.get(String(row.lab_reg_broker_id || ""));
    return info?.mobile || "";
  };

  // ✅ Helper to pull address from row OR fallback to master list
  const getBrokerAddress = (row) => {
    if (!row) return "";
    if (row.broker_address) return row.broker_address;
    if (row.lab_reg_broker_address) return row.lab_reg_broker_address;
    const info = brokerInfoMap.get(String(row.lab_reg_broker_id || ""));
    return info?.address || "";
  };

  const loadBrokers = async (search = "") => {
    try {
      setLoadingBrokers(true);
      const fd = new FormData();
      fd.append("search_key", search || "");
      fd.append("page_no", "none");
      const res = await axios.post(
        AppConst.BASE_URL + AppConst.getBrokerListUrl,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const records = Array.isArray(res?.data?.records) ? res.data.records : [];

      // ✅ Cache full list for mobile/address lookups
      setBrokerMasterList((prev) => {
        // Merge: keep existing entries, add new ones (search results may be partial)
        const map = new Map();
        prev.forEach((b) => {
          const id = String(b.broker_id ?? b.id ?? b.user_id ?? "");
          if (id) map.set(id, b);
        });
        records.forEach((b) => {
          const id = String(b.broker_id ?? b.id ?? b.user_id ?? "");
          if (id) map.set(id, b);
        });
        return Array.from(map.values());
      });

      const opts = records.map((r) => {
        const name =
          `${(r.fname || "").trim()}${r.lname ? " " + (r.lname || "").trim() : ""}`.trim() ||
          (r.mobile || "Unknown");
        const mobile = r.mobile ? ` — ${r.mobile}` : "";
        return {
          value: String(r.broker_id ?? r.id ?? r.user_id ?? ""),
          label: `${name}${mobile}`,
          raw: r,
        };
      });
      setBrokerOptions((prev) => {
        if (brokerId) {
          const exists = opts.find((o) => String(o.value) === String(brokerId));
          if (!exists) {
            return [
              { value: String(brokerId), label: brokerLabel || String(brokerId), raw: null },
              ...opts,
            ];
          }
        }
        return opts;
      });
    } catch (err) {
      console.error("loadBrokers", err);
      setBrokerOptions([]);
    } finally {
      setLoadingBrokers(false);
    }
  };

  useEffect(() => {
    loadBrokers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!brokerId) return;
    setBrokerOptions((prev) => {
      const found = prev.find((opt) => String(opt.value) === String(brokerId));
      if (found) {
        if (brokerLabel && found.label !== brokerLabel) {
          return prev.map((opt) =>
            String(opt.value) === String(brokerId) ? { ...opt, label: brokerLabel } : opt
          );
        }
        return prev;
      }
      return [
        { value: String(brokerId), label: brokerLabel || String(brokerId), raw: null },
        ...prev,
      ];
    });
  }, [brokerId, brokerLabel]);

  const fetchLabReferrals = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("search_key", formData.search_key || "");
      if (formData.from) fd.append("from", `${formData.from} 00:00:00`);
      if (formData.to) fd.append("to", `${formData.to} 23:59:59`);
      if (formData.lab_reg_referral_id)
        fd.append("lab_reg_referral_id", String(formData.lab_reg_referral_id));
      if (formData.lab_reg_referral)
        fd.append("lab_reg_referral", formData.lab_reg_referral);
      if (formData.lab_reg_broker_id)
        fd.append("lab_reg_broker_id", String(formData.lab_reg_broker_id));
      if (formData.lab_reg_broker_name)
        fd.append("lab_reg_broker_name", formData.lab_reg_broker_name);

      fd.append("department_filter", formData.department_filter || "all");
      fd.append("page_no", "none");

      const response = await axios.post(
        AppConst.BASE_URL + AppConst.getDoctorReferralLabUrl,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data?.status) {
        setFormData((prev) => ({ ...prev, records: response.data.records || [] }));
      } else {
        toast.error(response.data?.message || "Failed to fetch data");
        setFormData((prev) => ({ ...prev, records: [] }));
      }
    } catch (error) {
      console.error("❌ Error fetching lab referrals:", error);
      toast.error(
        "Failed to fetch lab referrals: " +
          (error.response?.data?.message || error.message)
      );
      setFormData((prev) => ({ ...prev, records: [] }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFormData((prev) => ({ ...prev, department_filter: activeSubTab }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab]);

  useEffect(() => {
    fetchLabReferrals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.department_filter,
    formData.from,
    formData.to,
    formData.lab_reg_referral_id,
    formData.lab_reg_referral,
    formData.lab_reg_broker_id,
    formData.lab_reg_broker_name,
    formData.search_key,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData((prev) => ({
      ...prev,
      search_key: "",
      from: getCurrentDate(),
      to: getCurrentDate(),
      lab_reg_referral_id: "",
      lab_reg_referral: "",
      lab_reg_broker_id: "",
      lab_reg_broker_name: "",
    }));
    setBrokerId("");
    setBrokerLabel("");
  };

  const openPayModal = (row) => {
    const { due } = getBrokerNumbers(row);
    setPayRow(row);
    setPayAmount(due > 0 ? due.toFixed(2) : "");
    setPayError("");
    setPayModalOpen(true);
  };

  const closePayModal = () => {
    setPayModalOpen(false);
    setPayRow(null);
    setPayAmount("");
    setPayError("");
  };

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    if (!payRow) return;
    const amt = parseFloat(payAmount || 0);
    if (isNaN(amt) || amt <= 0) {
      setPayError("Please enter a valid amount.");
      return;
    }

    try {
      setPayProcessing(true);
      setPayError("");

      const fd = new FormData();
      fd.append("lab_reg_id", payRow.lab_reg_id);
      fd.append("lab_reg_broker_id", payRow.lab_reg_broker_id || "");
      fd.append("pay_amount", amt.toFixed(2));
      fd.append("section", activeSubTab);

      const res = await axios.post(
        AppConst.BASE_URL + AppConst.payBrokerCommissionUrl,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data?.status) {
        toast.success(res.data?.message || "Broker payment saved.");
        closePayModal();
        fetchLabReferrals();
      } else {
        setPayError(res.data?.message || "Failed to save payment.");
      }
    } catch (err) {
      console.error("pay broker error", err);
      setPayError(err.response?.data?.message || err.message || "Failed to save payment.");
    } finally {
      setPayProcessing(false);
    }
  };

  const totals = useMemo(() => {
    const recs = formData.records || [];
    let totalAmount = 0,
      totalPaid = 0,
      totalBrokerCommission = 0,
      totalBrokerPaid = 0,
      totalBrokerDue = 0;

    recs.forEach((r) => {
      totalAmount += parseFloat(r.department_total || 0);
      totalPaid += parseFloat(r.department_paid || 0);
      const { commission, paid, due } = getBrokerNumbers(r);
      totalBrokerCommission += commission;
      totalBrokerPaid += paid;
      totalBrokerDue += due;
    });

    return {
      totalAmount: totalAmount.toFixed(2),
      totalPaid: totalPaid.toFixed(2),
      totalBrokerCommission: totalBrokerCommission.toFixed(2),
      totalBrokerPaid: totalBrokerPaid.toFixed(2),
      totalBrokerDue: totalBrokerDue.toFixed(2),
    };
  }, [formData.records]);

  const sectionLabel =
    activeSubTab === "pathology"
      ? "Pathology"
      : activeSubTab === "radiology"
      ? "Radiology"
      : "Total";

  const columns = useMemo(
    () => [
      { name: "SL No.", selector: (row, index) => index + 1, width: "70px" },
      {
        name: "Date",
        selector: (row) => {
          const dt = new Date(row.lab_reg_datetime);
          return Number.isNaN(dt.getTime()) ? 0 : dt.getTime();
        },
        sortable: true,
        width: "110px",
        cell: (row) => <span>{formatDate(row.lab_reg_datetime)}</span>,
      },
      {
        name: "UHID",
        selector: (row) => row.lab_reg_uhid || "N/A",
        sortable: true,
        wrap: true,
        width: "120px",
      },
      {
        name: "Patient Name",
        selector: (row) => row.lab_reg_name || "",
        sortable: true,
        wrap: true,
        cell: (row) => (
          <div style={{ whiteSpace: "normal" }}>{row.lab_reg_name || ""}</div>
        ),
        width: "180px",
      },
      {
        name: "Tests",
        selector: (row) => row.displayTestNames || "N/A",
        wrap: true,
        grow: 2,
        cell: (row) => (
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              maxWidth: "240px",
              fontSize: "0.9em",
            }}
          >
            {row.displayTestNames || "N/A"}
          </div>
        ),
        width: "220px",
      },
      {
        name: "Broker Name",
        selector: (row) => row.lab_reg_broker_name || "N/A",
        sortable: true,
        wrap: true,
        width: "160px",
        cell: (row) => (
          <div style={{ whiteSpace: "normal" }}>
            {row.lab_reg_broker_name || "N/A"}
          </div>
        ),
      },
      // ✅ NEW: Broker Mobile column
      {
        name: "Broker Mobile",
        selector: (row) => getBrokerMobile(row) || "N/A",
        sortable: true,
        width: "130px",
        cell: (row) => {
          const mobile = getBrokerMobile(row);
          return <span>{mobile || "N/A"}</span>;
        },
      },
      // ✅ NEW: Broker Address column
      {
        name: "Broker Address",
        selector: (row) => getBrokerAddress(row) || "",
        wrap: true,
        width: "180px",
        cell: (row) => {
          const addr = getBrokerAddress(row);
          return (
            <div
              style={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                fontSize: "0.85em",
              }}
            >
              {addr || "—"}
            </div>
          );
        },
      },
      {
        name: "Total Amount",
        selector: (row) => parseFloat(row.department_total || 0),
        sortable: true,
        right: true,
        cell: (row) => <span>₹{parseFloat(row.department_total || 0).toFixed(2)}</span>,
        width: "120px",
      },
      {
        name: "Total Paid",
        selector: (row) => parseFloat(row.department_paid || 0),
        sortable: true,
        right: true,
        cell: (row) => <span>₹{parseFloat(row.department_paid || 0).toFixed(2)}</span>,
        width: "120px",
      },
      {
        name: `Broker Comm. (${sectionLabel})`,
        selector: (row) => getBrokerNumbers(row).commission,
        sortable: true,
        right: true,
        cell: (row) => {
          const { commission } = getBrokerNumbers(row);
          return <span className="fw-bold text-primary">₹{commission.toFixed(2)}</span>;
        },
        width: "170px",
      },
      {
        name: "Paid",
        selector: (row) => getBrokerNumbers(row).paid,
        sortable: true,
        right: true,
        cell: (row) => {
          const { paid } = getBrokerNumbers(row);
          return <span>₹{paid.toFixed(2)}</span>;
        },
        width: "120px",
      },
      {
        name: "Due",
        selector: (row) => getBrokerNumbers(row).due,
        sortable: true,
        right: true,
        cell: (row) => {
          const { due } = getBrokerNumbers(row);
          return (
            <span className={due > 0 ? "text-danger fw-bold" : ""}>
              ₹{due.toFixed(2)}
            </span>
          );
        },
        width: "120px",
      },
      {
        name: "Status",
        cell: (row) => {
          const info = getBrokerStatusInfo(row);
          return (
            <span className={`badge rounded-pill ${info.className}`}>
              {info.status}
            </span>
          );
        },
        width: "110px",
      },
      {
        name: "Action",
        cell: (row) => {
          const { due } = getBrokerNumbers(row);
          const disabled = due <= 0.01;
          return (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => openPayModal(row)}
              disabled={disabled}
            >
              {disabled ? "Paid" : "Pay"}
            </button>
          );
        },
        width: "100px",
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sectionLabel, brokerInfoMap]
  );

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f1f5fb",
        color: "#405189",
        fontSize: "14px",
        fontWeight: 600,
      },
    },
    rows: { style: { fontSize: "14px", minHeight: "48px" } },
    cells: {
      style: {
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "10px",
        paddingRight: "10px",
      },
    },
  };

  const reactSelectStyles = {
    control: (base) => ({ ...base, minHeight: "38px", fontSize: "14px" }),
    option: (base) => ({ ...base, fontSize: "14px" }),
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("Lab Broker Commission Report", pageWidth / 2, 15, { align: "center" });

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    const subtitle = `Section: ${activeSubTab.toUpperCase()} | Period: ${formatDate(formData.from)} to ${formatDate(formData.to)}`;
    doc.text(subtitle, pageWidth / 2, 22, { align: "center" });

    const tableData = formData.records.map((row, index) => {
      const { commission, paid, due } = getBrokerNumbers(row);
      return [
        index + 1,
        formatDate(row.lab_reg_datetime),
        row.lab_reg_uhid || "N/A",
        row.lab_reg_name || "",
        row.displayTestNames || "N/A",
        row.lab_reg_broker_name || "N/A",
        getBrokerMobile(row) || "N/A",           // ✅ NEW
        getBrokerAddress(row) || "—",            // ✅ NEW
        commission.toFixed(2),
        paid.toFixed(2),
        due.toFixed(2),
      ];
    });

    // ✅ Footer row updated for new column count
    tableData.push([
      "",
      "",
      "",
      "",
      "",
      "TOTAL",
      "",
      "",
      totals.totalBrokerCommission,
      totals.totalBrokerPaid,
      totals.totalBrokerDue,
    ]);

    autoTable(doc, {
      startY: 28,
      head: [
        [
          "SL",
          "Date",
          "UHID",
          "Patient",
          "Tests",
          "Broker",
          "Mobile",      // ✅ NEW
          "Address",     // ✅ NEW
          `Comm (${sectionLabel})`,
          "Paid",
          "Due",
        ],
      ],
      body: tableData,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [64, 81, 137], textColor: 255, fontStyle: "bold", fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 8 },     // SL
        1: { cellWidth: 18 },    // Date
        2: { cellWidth: 20 },    // UHID
        3: { cellWidth: 28 },    // Patient
        4: { cellWidth: 35 },    // Tests
        5: { cellWidth: 28 },    // Broker
        6: { cellWidth: 22 },    // Mobile
        7: { cellWidth: 30 },    // Address
        8: { cellWidth: 22, halign: "right" },  // Comm
        9: { cellWidth: 18, halign: "right" },  // Paid
        10: { cellWidth: 18, halign: "right" }, // Due
      },
      didParseCell: (data) => {
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [241, 245, 251];
        }
      },
    });

    doc.save(`Lab_Broker_${activeSubTab}_${getCurrentDate()}.pdf`);
  };

  return (
    <div>
      <ul className="nav nav-pills nav-customs nav-primary mb-3" role="tablist">
        <li className="nav-item">
          <a
            className={`nav-link ${activeSubTab === "all" ? "active" : ""}`}
            onClick={(e) => { e.preventDefault(); setActiveSubTab("all"); }}
            href="#"
          >
            All
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link ${activeSubTab === "pathology" ? "active" : ""}`}
            onClick={(e) => { e.preventDefault(); setActiveSubTab("pathology"); }}
            href="#"
          >
            <i className="ri-microscope-line me-1 align-bottom"></i>
            Pathology
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link ${activeSubTab === "radiology" ? "active" : ""}`}
            onClick={(e) => { e.preventDefault(); setActiveSubTab("radiology"); }}
            href="#"
          >
            <i className="ri-scan-line me-1 align-bottom"></i>
            Radiology
          </a>
        </li>
      </ul>

      <div className="card-body border border-dashed border-end-0 border-start-0">
        <div className="row g-3 align-items-end">
          <div className="col-auto">
            <label className="form-label mb-1">From</label>
            <input
              type="date"
              className="form-control"
              name="from"
              onChange={handleChange}
              value={formData.from}
              style={{ width: "150px" }}
            />
          </div>

          <div className="col-auto">
            <label className="form-label mb-1">To</label>
            <input
              type="date"
              className="form-control"
              name="to"
              onChange={handleChange}
              value={formData.to}
              style={{ width: "150px" }}
            />
          </div>

          <div className="col-auto" style={{ minWidth: "260px" }}>
            <label className="form-label mb-1">Broker</label>
            <Select
              options={brokerOptions}
              value={
                brokerOptions.find(
                  (o) => String(o.value) === String(formData.lab_reg_broker_id)
                ) || null
              }
              onChange={(opt) => {
                if (!opt) {
                  setFormData((prev) => ({
                    ...prev,
                    lab_reg_broker_id: "",
                    lab_reg_broker_name: "",
                  }));
                  setBrokerId("");
                  setBrokerLabel("");
                } else {
                  const cleanName = opt.label.split("—")[0].trim();
                  setFormData((prev) => ({
                    ...prev,
                    lab_reg_broker_id: String(opt.value),
                    lab_reg_broker_name: cleanName,
                  }));
                  setBrokerId(String(opt.value));
                  setBrokerLabel(opt.label);
                }
              }}
              onInputChange={(val, { action }) => {
                if (action === "input-change") loadBrokers(val);
              }}
              isClearable
              placeholder="All Brokers"
              styles={reactSelectStyles}
              isLoading={loadingBrokers}
            />
          </div>

          <div className="col" style={{ minWidth: "250px" }}>
            <label className="form-label mb-1">Search</label>
            <div className="search-box">
              <input
                className="form-control search"
                name="search_key"
                onChange={handleChange}
                value={formData.search_key}
                placeholder="Search by name, UHID, mobile..."
                type="text"
              />
              <i className="ri-search-line search-icon"></i>
            </div>
          </div>

          <div className="col-auto">
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-secondary" onClick={handleReset}>
                <i className="ri-refresh-line me-1"></i> Clear
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleExportPDF}
                disabled={formData.records.length === 0}
              >
                <i className="ri-file-pdf-line me-1"></i> Export PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body pt-3">
        <DataTable
          columns={columns}
          data={formData.records}
          customStyles={customStyles}
          pagination
          highlightOnHover
          dense
          persistTableHead
          progressPending={loading}
          noDataComponent={<div className="text-muted py-3">No records found</div>}
        />

        {formData.records.length > 0 && (
          <div className="d-flex justify-content-end mt-3">
            <div className="text-muted" style={{ fontSize: "0.95rem" }}>
              <strong>Total Amount:</strong> ₹{totals.totalAmount} &nbsp;|&nbsp;
              <strong>Total Paid:</strong> ₹{totals.totalPaid} &nbsp;|&nbsp;
              <strong>Broker Comm. ({sectionLabel}):</strong> ₹{totals.totalBrokerCommission} &nbsp;|&nbsp;
              <strong>Broker Paid:</strong> ₹{totals.totalBrokerPaid} &nbsp;|&nbsp;
              <strong>Broker Due:</strong> ₹{totals.totalBrokerDue}
            </div>
          </div>
        )}
      </div>

      {payModalOpen && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handlePaySubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    Pay Broker Commission ({sectionLabel})
                  </h5>
                  <button type="button" className="btn-close" onClick={closePayModal}></button>
                </div>
                <div className="modal-body">
                  {payRow && (
                    <>
                      <p className="mb-2">
                        <strong>Broker:</strong> {payRow.lab_reg_broker_name || "N/A"}
                      </p>
                      <p className="mb-2">
                        <strong>Patient:</strong> {payRow.lab_reg_name} ({payRow.lab_reg_uhid})
                      </p>
                      <p className="mb-2">
                        <strong>Section:</strong> {sectionLabel}
                      </p>
                      <p className="mb-2">
                        <strong>Commission:</strong> ₹{getBrokerNumbers(payRow).commission.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Paid:</strong> ₹{getBrokerNumbers(payRow).paid.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Due:</strong> ₹{getBrokerNumbers(payRow).due.toFixed(2)}
                      </p>
                      <div className="mb-3">
                        <label className="form-label">Pay Amount (₹)</label>
                        <input
                          type="number"
                          className="form-control"
                          step="0.01"
                          min="0"
                          value={payAmount}
                          onChange={(e) => setPayAmount(e.target.value)}
                        />
                      </div>
                      {payError && <div className="alert alert-danger py-2">{payError}</div>}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={closePayModal} disabled={payProcessing}>
                    Close
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={payProcessing}>
                    {payProcessing ? "Saving..." : "Save Payment"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabReferral;